import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';
import { createDecimal } from '@chiroup/core/functions/createDecimal';
import {
  BillingCodeReference,
  CodeLookupOptions,
  CodeSets,
} from '@chiroup/core/types/BillingCode.type';
import { useEffect, useState } from 'react';
import { QueryFunctionContext, useInfiniteQuery } from 'react-query';
import { useDebounce } from './useDebounce';

const query = (endpoint?: string, local?: boolean) => {
  return async (context: QueryFunctionContext) => {
    const clinicId = context.queryKey[1];
    const type = Array.isArray(context.queryKey[2])
      ? context.queryKey[2].join('|')
      : context.queryKey[2];
    const search = context.queryKey[3];
    const disregardSearch = context.queryKey[6];
    if (context.queryKey[8] && !context.queryKey[7]) return { count: 0, data: [], page: -1 };
    if (!search && !disregardSearch) return { count: -1, data: [], page: -1 };

    return ChiroUpAPI.get('api', endpoint ? endpoint : `/settings/${clinicId}/codes`, {
      queryParams: {
        type,
        search,
        weighted: context.queryKey[4],
        page: context.pageParam,
        local,
        serviceDate: context.queryKey[7],
        requireServiceDate: context.queryKey[8],
        markClinicCodes: context.queryKey[9],
        splitBilledAmounts: context.queryKey[10],
      },
    });
  };
};

type UseCodesParamType = {
  clinicId?: number;
  type: CodeSets | CodeSets[];
  weighted?: boolean | undefined;
  endpoint?: string | undefined;
  search: string;
  local?: boolean;
  disregardSearch?: boolean;
  lookupOptions?: CodeLookupOptions;
  /** Library results get clinicBillingCodeId when the clinic already has the code. */
  markClinicCodes?: boolean;
  /** One option per billed amount (S9090 DRX / Decomp), for pickers that add service lines. */
  splitBilledAmounts?: boolean;
};

export const useCodes = (params: UseCodesParamType) => {
  const [restError, setRestError] = useState<any | null>(null);
  const { type, weighted, endpoint, local = false, disregardSearch = false } = params;
  const search = useDebounce(params.search, 750);
  const missingServiceDate =
    !!params.lookupOptions?.requireServiceDate && !params.lookupOptions.serviceDate;
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<{
    data: BillingCodeReference[];
    count: number;
    page: number;
  }>(
    [
      'codes',
      params.clinicId,
      type,
      search,
      weighted,
      local,
      disregardSearch,
      params.lookupOptions?.serviceDate,
      params.lookupOptions?.requireServiceDate,
      params.markClinicCodes,
      params.splitBilledAmounts,
    ],
    query(endpoint, local),
    {
      enabled: !missingServiceDate,
      getNextPageParam: lastGroup =>
        (lastGroup || {}).page ? (lastGroup || {}).page + 1 : undefined,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 0,
      onError: e => {
        setRestError(e);
      },
    }
  );

  const [options, setOptions] = useState<
    {
      value: any;
      text: string | React.ReactNode;
      disabled?: boolean;
    }[]
  >([]);

  useEffect(() => {
    let recs: any = [];

    try {
      if (data) {
        recs = data?.pages
          .reduce((acc, curr) => {
            return [...acc, ...curr.data];
          }, [] as BillingCodeReference[])
          .map(code =>
            code.billedAmountId || code.multipleBilledAmounts
              ? {
                  // 13052385647: a code's billed amounts share code.code, so the value needs the
                  // id; the code's own amount has none.
                  text: `${code.code} (${code.shortcut}, $${createDecimal(code.billedAmount ?? 0).toFixed(2)}) - ${code.description}`,
                  value: `${code.code}#${code.billedAmountId ?? ''}`,
                  data: {
                    description: code.description,
                    codeSet: code.codeSet,
                    validFrom: code.validFrom,
                    validUntil: code.validUntil,
                    code: code.code,
                    billedAmountId: code.billedAmountId,
                    billedAmount: code.billedAmount,
                    shortcut: code.shortcut,
                  },
                }
              : {
                  text: `${code.code} - ${code.description}`,
                  value: code.code,
                  data: {
                    description: code.description,
                    codeSet: code.codeSet,
                    validFrom: code.validFrom,
                    validUntil: code.validUntil,
                  },
                }
          );
      }

      setOptions(recs);
    } catch (e: any) {
      setRestError(e);
      console.error({ e });
    }
  }, [data]);

  return {
    data,
    options: missingServiceDate ? [] : options,
    setOptions,
    isFetching,
    fetchNextPage,
    hasNextPage,
    restError,
    setRestError,
    isFetchingNextPage,
  };
};
